import {Component, Input, OnInit} from '@angular/core';
import {SbDateTime, SbLocalDate} from '../../services/api/api-date-time';
import {DateTime} from 'luxon';
import {
  AbstractControl,
  FormControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {DailyHourRangeValidatorService} from '../../services/date-time/daily-hour-range-validator.service';
import {DayTimeHoursDTO, DayTimeIntervalDTO} from '../../services/api/api';

@Component({
  selector: 'sb-comp-special-weekly-schedule-input',
  templateUrl: './sb-special-opening-hours-input.component.html',
  styleUrls: ['./sb-special-opening-hours-input.component.scss'],
  standalone: false,
})
export class SbSpecialOpeningHoursInputComponent implements OnInit {
  @Input() overrides: any[] = [];

  daysValid = [];
  newDateInputValue: SbLocalDate;
  form = new UntypedFormGroup({
    Name: new FormControl(null, [Validators.required]),
  });

  constructor(private rangeValidator: DailyHourRangeValidatorService) {}

  ngOnInit(): void {
    this.overrides.forEach(override => {
      if (override.dailyHourRange?.hourRanges) {
        let c = 0;
        override.dailyHourRange.hourRanges.forEach(() => {
          this.addFormControl(override, c);
          c++;
        });
      }
    });
  }

  addFormControl(override: any, index: number) {
    const overrideIndex = this.overrides.indexOf(override);
    const r = override.dailyHourRange.hourRanges[index];

    const fromControl = new UntypedFormControl(('00' + r.fromHour).slice(-2) + ':' + ('00' + r.fromMinute).slice(-2), [
      Validators.required,
      this.timeInputValidator(overrideIndex, index, 'from'),
    ]);
    const tillControl = new UntypedFormControl(('00' + r.tillHour).slice(-2) + ':' + ('00' + r.tillMinute).slice(-2), [
      Validators.required,
      this.timeInputValidator(overrideIndex, index, 'till'),
    ]);

    const name = '' + overrideIndex + '_' + index;
    this.form.addControl(name + '_from', fromControl);
    this.form.addControl(name + '_till', tillControl);
  }

  removeFormControl(overrideIndex: number, index: number) {
    this.form.removeControl(overrideIndex + '_' + index + '_from');
    this.form.removeControl(overrideIndex + '_' + index + '_till');
  }

  toggle(overrideIndex: number, $event) {
    if (this.overrides[overrideIndex].dailyHourRange) {
      this.daysValid[overrideIndex] = true;
      this.overrides[overrideIndex].dailyHourRange = null;
    } else {
      this.addTimeRange(overrideIndex);
    }
    $event.preventDefault();
    $event.stopPropagation();
  }

  addTimeRange(overrideIndex: number): void {
    if (!this.overrides[overrideIndex].dailyHourRange) {
      this.overrides[overrideIndex].dailyHourRange = new DayTimeHoursDTO();
      this.daysValid[overrideIndex] = true;
    }

    if (this.overrides[overrideIndex].dailyHourRange.hourRanges === null) {
      this.overrides[overrideIndex].dailyHourRange.hourRanges = [];
    }

    const hourRange = new DayTimeIntervalDTO();
    hourRange.fromHour = 0;
    hourRange.fromMinute = 0;
    hourRange.tillHour = 23;
    hourRange.tillMinute = 0;

    this.overrides[overrideIndex].dailyHourRange.hourRanges.push(hourRange);

    const rangeIndex = this.overrides[overrideIndex].dailyHourRange.hourRanges.length - 1;
    this.addFormControl(this.overrides[overrideIndex], rangeIndex);
  }

  removeTimeRange(overrideIndex: number, index: number): void {
    const dailyHourRange = this.overrides[overrideIndex].dailyHourRange;
    if (dailyHourRange?.hourRanges) {
      if (dailyHourRange.hourRanges.length > 1) {
        this.removeFormControl(overrideIndex, index);
        delete dailyHourRange.hourRanges[index];
        dailyHourRange.hourRanges = dailyHourRange.hourRanges.filter(el => el != null);
      } else {
        this.overrides[overrideIndex].dailyHourRange = null;
        this.daysValid[overrideIndex] = true;
      }
    }
  }

  onChange(overrideIndex: number, rangeIndex: number, rangeType: string, $event) {
    const [hour, minute] = this.rangeValidator.getHourAndMinuteFromTimeString($event?.target?.value);

    this.overrides[overrideIndex].dailyHourRange.hourRanges[rangeIndex][rangeType + 'Hour'] = hour;
    this.overrides[overrideIndex].dailyHourRange.hourRanges[rangeIndex][rangeType + 'Minute'] = minute;

    $event.target.value = this.getTimeString(overrideIndex, rangeIndex, rangeType);
  }

  getTimeString(overrideIndex: number, rangeIndex: number, rangeType: string) {
    const hourRange = this.overrides[overrideIndex].dailyHourRange.hourRanges[rangeIndex];
    return ('00' + hourRange[rangeType + 'Hour']).slice(-2) + ':' + ('00' + hourRange[rangeType + 'Minute']).slice(-2);
  }

  timeInputValidator(overrideIndex: number, rangeIndex: number, type: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const time = control.value;
      let [hour, minute] = time.split(':');
      hour = +hour;
      minute = +minute;

      // copy of range
      if (this.overrides?.[overrideIndex]?.dailyHourRange?.hourRanges?.[rangeIndex]) {
        const r = JSON.parse(JSON.stringify(this.overrides[overrideIndex].dailyHourRange.hourRanges[rangeIndex]));
        r[type + 'Hour'] = hour;
        r[type + 'Minute'] = minute;
        if (r.fromHour > r.tillHour || (r.fromHour === r.tillHour && r.fromMinute >= r.tillMinute)) {
          return { invalidTime: true };
        }
      }

      if (isNaN(hour) || isNaN(minute)) {
        return { invalidTime: true };
      }

      return null;
    };
  }

  hasError(overrideIndex: number, index: number, type: string): boolean {
    const input = this.form.get(overrideIndex + '_' + index + '_' + type);

    if (input) {
      return !!(input.touched && input.errors);
    }
    return false;
  }

  hasValidRanges(overrideIndex: number): boolean {
    let valid;
    const dayRanges = this.overrides[overrideIndex]?.dailyHourRange?.hourRanges;
    if (dayRanges) {
      valid = this.rangeValidator.hasValidIntervals(dayRanges);
    } else {
      valid = true;
    }
    this.daysValid[overrideIndex] = valid;
    return valid;
  }

  removeOverride(index: number) {
    const newOverrides = [];
    this.overrides.forEach(o => {
      if (o !== this.overrides[index]) {
        newOverrides.push(o);
      }
    });
    this.overrides = newOverrides;
  }

  newDateInputChanged($event) {
    this.newDateInputValue = $event;
  }

  addDateFromInput() {
    if (this.newDateInputValue) {
      const override: any = {};
      override.date = new SbDateTime(DateTime.fromISO(this.newDateInputValue.toString()));

      const index = this.overrides.length;
      this.overrides.push(override);
      this.addTimeRange(index);

      this.newDateInputValue = null;
    }
  }

  isNewDateInputValid() {
    let valid = true;
    if (this.newDateInputValue) {
      const newDateString = new SbDateTime(DateTime.fromISO(this.newDateInputValue.toString()));
      this.overrides.forEach(o => {
        if (o.date.toString() === newDateString.toString()) {
          valid = false;
        }
      });
      return valid;
    }
    return false;
  }
}
